import React, { useMemo, useState, useEffect } from 'react';
import { convertVolume } from './consumptionUtils';
import { updateDepassementThreshold } from './cameraApi';
import Toast from './Toast';

const AlertSettings = ({ motor, user, setMotor, loadingMotor, dailyTotals }) => {
  const [thresholdUnit, setThresholdUnit] = useState('m3');
  const [thresholdInput, setThresholdInput] = useState('0.5');
  const [savedThresholdM3, setSavedThresholdM3] = useState(0.5);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ type: '', message: '' });

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast({ type: '', message: '' }), 3000);
  };

  useEffect(() => {
    if (typeof motor.depassement === 'number') {
      setSavedThresholdM3(motor.depassement);
      setThresholdInput(motor.depassement.toString());
    }
  }, [motor.depassement]);

  const handleUnitChange = (newUnit) => {
    const current = parseFloat(thresholdInput);
    if (!isNaN(current)) {
      const currentM3 = thresholdUnit === 'litre' ? current / 1000 : current;
      const converted = newUnit === 'litre' ? currentM3 * 1000 : currentM3;
      setThresholdInput(parseFloat(converted.toFixed(3)).toString());
    }
    setThresholdUnit(newUnit);
  };

  const handleSave = async () => {
    const value = parseFloat(thresholdInput);
    if (isNaN(value) || value <= 0) {
      showToast('error', 'Valeur de seuil invalide.');
      return;
    }
    setSaving(true);
    try {
      const valueM3 = thresholdUnit === 'litre' ? value / 1000 : value;
      const updatedCamera = await updateDepassementThreshold(motor.id, valueM3, user.token);
      setSavedThresholdM3(valueM3);
      setMotor((prev) => ({ ...prev, depassement: updatedCamera.depassement }));
      showToast('success', 'Seuil enregistré !');
    } catch (err) {
      console.error('Erreur lors de la mise à jour du seuil de dépassement', err);
      showToast('error', "Échec de l'enregistrement du seuil.");
    } finally {
      setSaving(false);
    }
  };

  const overruns = useMemo(
    () => dailyTotals.filter((d) => d.totalM3 > savedThresholdM3),
    [dailyTotals, savedThresholdM3]
  );

  const thresholdLabel = thresholdUnit === 'litre' ? 'L' : 'm³';

  return (
    <div className="bg-white border border-gray-200 shadow-lg rounded-lg p-6 sm:p-8 mb-8">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-400 to-orange-400 flex items-center justify-center shrink-0">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l6.028 10.72c.75 1.334-.213 2.981-1.743 2.981H3.972c-1.53 0-2.493-1.647-1.743-2.98L8.257 3.1zM11 14a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V7a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        </div>
        <h2 className="text-xl sm:text-2xl font-semibold text-gray-800">Alerte de Dépassement</h2>
      </div>

      <div className="bg-gray-50 rounded-xl p-6 mb-6">
        <label className="block text-sm font-medium text-gray-600 mb-3">
          Volume de dépassement autorisé par jour
        </label>
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="number"
            step="0.001"
            min="0"
            value={thresholdInput}
            onChange={(e) => setThresholdInput(e.target.value)}
            className="border border-gray-300 p-3 rounded-lg w-40 shadow-sm focus:outline-none focus:ring-2 focus:ring-red-400"
          />
          <div className="flex bg-white border border-gray-300 rounded-lg overflow-hidden shadow-sm">
            <button
              onClick={() => handleUnitChange('m3')}
              className={`px-4 py-3 text-sm font-medium transition-colors ${
                thresholdUnit === 'm3' ? 'bg-red-500 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              m³
            </button>
            <button
              onClick={() => handleUnitChange('litre')}
              className={`px-4 py-3 text-sm font-medium transition-colors ${
                thresholdUnit === 'litre' ? 'bg-red-500 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Litres
            </button>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-gradient-to-r from-red-500 to-orange-500 text-white px-6 py-3 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 font-medium disabled:opacity-50"
          >
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-3">
          Seuil actuel appliqué : {parseFloat(convertVolume(savedThresholdM3, thresholdUnit).toFixed(3))} {thresholdLabel} / jour
        </p>
        <Toast type={toast.type} message={toast.message} />
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Historique des dépassements</h3>
          <span
            className={`text-sm font-semibold px-3 py-1 rounded-full ${
              overruns.length > 0 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
            }`}
          >
            {overruns.length > 0 ? `${overruns.length} dépassement(s)` : 'Aucun dépassement'}
          </span>
        </div>

        {loadingMotor ? (
          <p className="text-gray-400 text-sm py-4">Chargement...</p>
        ) : overruns.length === 0 ? (
          <p className="text-gray-400 text-sm py-4">Aucune journée n'a dépassé le seuil défini pour le moment.</p>
        ) : (
          <ul className="space-y-2 max-h-80 overflow-y-auto pr-1">
            {overruns.map((day) => {
              const ratio = Math.min(day.totalM3 / savedThresholdM3, 2);
              const barPercent = Math.min((ratio / 2) * 100, 100);
              return (
                <li
                  key={day.dateKey}
                  className="flex items-center justify-between gap-4 bg-red-50 border-l-4 border-red-400 rounded-lg px-4 py-3"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800">
                      {day.date.toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}
                    </p>
                    <div className="w-full bg-red-100 rounded-full h-1.5 mt-2">
                      <div className="bg-red-500 h-1.5 rounded-full" style={{ width: `${barPercent}%` }} />
                    </div>
                  </div>
                  <span className="text-sm font-bold text-red-700 whitespace-nowrap">
                    {parseFloat(convertVolume(day.totalM3, thresholdUnit).toFixed(3))} {thresholdLabel}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AlertSettings;